/**
 * Question Type Definitions — Governance Sidecar
 *
 * ARCHITECTURE v5 (mei 2026): This file is no longer a parallel registry —
 * it is a GOVERNANCE SIDECAR that augments the runtime question-type registry
 * (src/content/questions/questionTypeRegistry.ts) with governance-only
 * metadata: cognitiveCeiling, category, dataStructure rules, structuredDataField,
 * validationRules, examples.
 *
 * SSOT for "which types exist" = the runtime registry (QUESTION_TYPE_META).
 * A type registered there but NOT in this sidecar:
 *   - Still works at runtime (renderer fires).
 *   - Gets SAFE DEFAULTS in governance (DOK2 ceiling, category 'interactive').
 *   - Triggers a dev-time console.warn so the sidecar can be enriched.
 *
 * This means: adding a new interactive question type no longer requires
 * touching this file just to stop false-positive DIVERSITY_NO_INTERACTIVE
 * flags. The diversity stage now reads `isInteractive` directly from the
 * runtime registry. This sidecar's job is only to refine governance
 * decisions (DOK3-capable? structuredDataField? required data fields?).
 *
 * Central registry mapping question types to their requirements and validation rules.
 *
 * This is the single source of truth for:
 * - What governance fields each question type requires
 * - Cognitive ceiling per type (DOK3 diversity rule)
 * - What visual aids are appropriate
 * - Type-specific validation rules
 */

import { QuestionType } from '../content'
import type { VisualAidType } from '../visuals'
import { QUESTION_TYPE_META } from '../content/questions/questionTypeRegistry'

export interface QuestionTypeDefinition {
  type: QuestionType
  category: 'standard' | 'interactive' | 'visual-data' | 'specialized'
  renderer: string
  description: string
  requiredFields: string[]
  optionalFields: string[]
  /**
   * Highest Depth-of-Knowledge level this question type is structurally
   * capable of probing. Used by the diversity stage (DIV-005) and by
   * pedagogy heuristics. SSOT for "is this type DOK3-capable?".
   *
   * - DOK1 = recall / reproduction only
   * - DOK2 = skill / concept application
   * - DOK3 = strategic / analytical / evaluative thinking
   *
   * Note: a type's ceiling does not force the actual DOK of any single
   * question — a step_solver question can still be tagged DOK2 if its
   * content is simple. The ceiling means: "this format CAN reach this DOK".
   */
  cognitiveCeiling: 'DOK1' | 'DOK2' | 'DOK3'
  /**
   * Name of the namespaced `question.data.<field>` key this type REQUIRES
   * to be present. When set, the question MUST carry structured operands
   * in that field — falling back to regex-parsing operands out of the
   * question text is a fragile anti-pattern (PEDAGOGICAL_CONTRACT_SPEC §7).
   *
   * Used by pedagogyStage.detectsRegexFallback as SSOT — adding a new
   * arithmetic/data-first type only requires setting this property here,
   * the pipeline picks it up automatically.
   *
   * Examples:
   *   - 'cijferend_rekenen' / 'cijferend_aftrekken' / 'cijferend_vermenigvuldigen'
   *     → 'columnArithmetic'
   *   - 'cijferend_delen' → 'longDivision'
   *
   * Omit for types that don't have an arithmetic operand contract.
   */
  structuredDataField?: 'columnArithmetic' | 'longDivision'
  /**
   * GSR-18 (audit 2026, PR-6): some question types reuse another type's
   * nested `data.<key>` shape. When set, `resolveTypeData()` in typeStage
   * unwraps `data[dataKeyAlias]` instead of `data[questionType]`.
   *
   * Example: `clock_set` (set-of-clocks variant) carries its payload under
   * `data.clock` — the same shape as the singular `clock` type — so
   * `dataKeyAlias: 'clock'` lets the type-stage resolver find it without
   * needing a hardcoded alias map.
   *
   * Replaces the inline `TYPE_DATA_ALIASES` table that previously lived in
   * `typeStage.ts`. Keep this rare and well-justified — every alias is a
   * tiny implicit coupling between two type contracts.
   */
  dataKeyAlias?: string
  /**
   * GSR-18 (audit 2026, PR-6): true when this type has a dedicated
   * `validateXxx()` function (in typeStage's typeValidators/*) that
   * comprehensively checks data shape — including multiple legacy/canonical
   * variants. When true, typeStage's generic required-field check is
   * SKIPPED for this type, since the dedicated validator already covers
   * (and accepts richer shapes than) the generic check.
   *
   * Examples: `bar_chart`, `line_chart`, `pie_chart`, `matching`,
   * `text_highlight` — each accepts multiple equivalent data shapes that
   * the generic check would otherwise flag as missing fields.
   *
   * NOTE: `strategy_solver` is intentionally NOT a `hasComprehensiveValidator`
   * type — its opt-out is a *nested-key unwrapping* problem (see typeStage
   * inline comment), not a multi-shape problem. Until a dedicated
   * `unwrapsToNestedKey` sidecar exists, it stays inline-listed in typeStage.
   *
   * Replaces the inline `TYPES_WITH_COMPREHENSIVE_VALIDATION` Set that
   * previously lived in `typeStage.ts`.
   */
  hasComprehensiveValidator?: boolean
  dataStructure?: {
    required: string[]
    optional: string[]
    /**
     * Alias groups — each inner array is a set of equivalent field names
     * (e.g. legacy singular vs. plural form). The group is considered
     * satisfied if AT LEAST ONE field in it is present on the resolved
     * data object.
     *
     * Use this for types whose seed shape evolved over time and accepts
     * multiple equivalent shapes, instead of falling back to a comprehensive
     * type-specific validator opt-out.
     *
     * Example (number_sequence accepts singular OR plural forms):
     *   requiredAnyOf: [
     *     ['missingIndex', 'missingIndices'],
     *     ['correctValue', 'correctValues'],
     *   ]
     */
    requiredAnyOf?: string[][]
  }
  visualRequirements?: {
    stimulus?: VisualAidType[]
    support?: VisualAidType[]
  }
  validationRules: string[]
  examples: string[]
}

/**
 * Governance sidecar — partial map of question type → governance metadata.
 *
 * Entries are OPTIONAL. A type listed in the runtime registry but absent here
 * gets safe defaults (DOK2 ceiling, interactive category, minimal requireds).
 *
 * The values still carry a `type` field for readability and to keep the
 * existing QuestionTypeDefinition shape — runtime build merges in the key.
 */
const SIDECAR_METADATA: Partial<Record<QuestionType, QuestionTypeDefinition>> =
  {
    // ─── STANDARD TYPES ─────────────────────────────────────────────────────────

    multiple_choice: {
      type: 'multiple_choice',
      category: 'standard',
      renderer: 'src/components/practice/renderers/MultipleChoiceRenderer.tsx',
      description: 'Standard multiple choice with 2-5 options',
      requiredFields: ['question', 'options', 'correctAnswer'],
      optionalFields: ['context', 'stimulusAids', 'supportAids'],
      cognitiveCeiling: 'DOK2',
      validationRules: [
        'options.length >= 2 && options.length <= 5',
        'correctAnswer >= 0 && correctAnswer < options.length',
        'All options must be non-empty strings',
        'No duplicate options',
      ],
      examples: ['GET-BREUK-BASIS', 'TIJD-ANALOOG', 'SWW-TT-STAM'],
    },

    // ─── INTERACTIVE TYPES ──────────────────────────────────────────────────────

    verb_conjugation_flow: {
      type: 'verb_conjugation_flow',
      category: 'interactive',
      renderer:
        'src/components/practice/renderers/VerbConjugationFlowRenderer.tsx',
      description:
        'Interactive decision-tree for verb conjugation (e.g. kofschip, hebben/zijn)',
      requiredFields: ['question'],
      optionalFields: ['context', 'stimulusAids', 'supportAids'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['verb', 'targetForm', 'steps', 'finalAnswer'],
        optional: ['context'],
      },
      validationRules: [
        'steps must be an array of at least 2 steps',
        'choice steps must have options with exactly one isCorrect: true',
        'input steps must have acceptedAnswers array',
      ],
      examples: ['SWW-VD-HULPWW', 'SWW-VT-VZFS'],
    },

    fill_in: {
      type: 'fill_in',
      category: 'interactive',
      renderer: 'src/components/practice/renderers/FillInRenderer.tsx',
      description: 'Text input field for short answers',
      requiredFields: ['question', 'correctAnswer'],
      optionalFields: [
        'context',
        'acceptableAnswers',
        'caseSensitive',
        'stimulusAids',
        'supportAids',
      ],
      cognitiveCeiling: 'DOK1',
      dataStructure: {
        required: [],
        optional: [
          'acceptedAnswers',
          'caseSensitive',
          'placeholder',
          'inputType',
        ],
      },
      validationRules: [
        'correctAnswer must be a string or number',
        'If data.acceptedAnswers provided, must be array of strings',
        'If data.caseSensitive provided, must be boolean',
      ],
      examples: [
        'SWW-TT-STAM (fill-in variant)',
        'SPR-WEET-EI-IJ',
        'GET-KOMMA-AFRONDEN (fill-in variant)',
      ],
    },

    drag_drop_fill: {
      type: 'drag_drop_fill',
      category: 'interactive',
      renderer: 'src/components/practice/renderers/DragDropFillRenderer.tsx',
      description: 'Drag words/phrases into blanks in text',
      requiredFields: ['question', 'data'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['template', 'draggables', 'correctPlacements'],
        optional: ['distractors'],
      },
      validationRules: [
        'data.template must contain {0}, {1}, etc. placeholders',
        'data.draggables must be array of strings',
        'data.correctPlacements must map placeholder indices to draggable indices',
      ],
      examples: ['TAA-ONT-BASIS', 'SPR-KLANK-JAGER'],
    },

    matching: {
      type: 'matching',
      category: 'interactive',
      renderer: 'src/components/practice/renderers/MatchingRenderer.tsx',
      description: 'Match items from left column to right column',
      requiredFields: ['question', 'data'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      // GSR-18 (PR-6): validateMatching accepts pairs/canonical/nested shapes.
      hasComprehensiveValidator: true,
      dataStructure: {
        required: [],
        optional: [
          'leftItems',
          'rightItems',
          'correctMatches',
          'distractors',
          'pairs',
        ],
      },
      validationRules: [
        'Supports two data formats:',
        'Format A (pairs): data.pairs = [{left, right}, ...] — at least 2 pairs',
        'Format B (canonical): data.leftItems + data.rightItems + data.correctMatches',
        'Also accepts nested: data.matching.pairs = [{left, right}, ...]',
        'All indices must be valid',
      ],
      examples: ['TAA-SOORT-WW', 'GET-VERH-BREUK-PROC-KOPPEL', 'TOPO-NL-PROV'],
    },

    ordering: {
      type: 'ordering',
      category: 'interactive',
      renderer: 'src/components/practice/renderers/OrderingRenderer.tsx',
      description: 'Arrange items in correct order',
      requiredFields: ['question', 'data'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['items', 'correctOrder'],
        optional: [],
      },
      validationRules: [
        'data.items must be array of strings',
        'data.correctOrder must be array of indices',
        'correctOrder must be a permutation of [0, 1, 2, ...]',
      ],
      examples: ['STU-ALFABET', 'MET-TIJD-NAUWKEURIG'],
    },

    table_reading: {
      type: 'table_reading',
      category: 'interactive',
      renderer: 'src/components/practice/renderers/TableReadingRenderer.tsx',
      description: 'Read and interpret data from tables',
      requiredFields: ['question', 'data', 'options', 'correctAnswer'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['headers', 'rows'],
        optional: ['caption', 'highlightCell'],
      },
      validationRules: [
        'data.headers must be array of strings',
        'data.rows must be array of arrays',
        'All rows must have same length as headers',
      ],
      examples: ['STU-TABEL', 'TIJD-SCHEMA'],
    },

    number_sequence: {
      type: 'number_sequence',
      category: 'interactive',
      renderer: 'src/components/practice/renderers/NumberSequenceRenderer.tsx',
      description: 'Complete number sequences or patterns',
      requiredFields: ['question', 'data'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        // Always required.
        required: ['sequence'],
        // Legacy/plural aliases — accept either the singular or the plural form.
        // AI_SEED_GENERATION_RULES.md documents the plural shape; older seeds
        // (and the runtime validator) still accept the singular. The generic
        // type check honours `requiredAnyOf` so neither shape produces a
        // false-positive TYPE_MISSING_DATA_FIELD.
        requiredAnyOf: [
          ['missingIndex', 'missingIndices'],
          ['correctValue', 'correctValues'],
        ],
        optional: ['rule'],
      },
      validationRules: [
        'data.sequence must be array of numbers or null',
        'data.missingIndex / data.missingIndices must be valid index(es)',
        'data.correctValue / data.correctValues must be a number or array of numbers',
      ],
      examples: ['GET-GROOT-POS', 'GET-KOMMA-GETALLENLIJN'],
    },

    ratio_table: {
      type: 'ratio_table',
      category: 'interactive',
      renderer: 'src/components/practice/renderers/RatioTableRenderer.tsx',
      description: 'Complete ratio tables with proportional reasoning',
      requiredFields: ['question', 'data'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['headers', 'rows', 'hiddenCells'],
        optional: [],
      },
      validationRules: [
        'data.headers must be array of 2 strings',
        'data.rows must be array of [number, number] pairs',
        'data.hiddenCells must be array of {row, col} objects',
        'Each hiddenCell must reference a valid cell in the rows array',
        'Ratio must be consistent across rows',
      ],
      visualRequirements: {
        stimulus: ['ratio'],
        support: ['ratio'],
      },
      examples: ['GET-VERH-RECEPT', 'GET-VERH-SCHAAL'],
    },

    text_highlight: {
      type: 'text_highlight',
      category: 'interactive',
      renderer: 'src/components/practice/renderers/TextHighlightRenderer.tsx',
      description: 'Highlight specific words or phrases in text',
      requiredFields: ['question', 'data'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      // GSR-18 (PR-6): validateTextHighlight accepts position + segment shapes.
      hasComprehensiveValidator: true,
      dataStructure: {
        required: [],
        optional: ['text', 'correctHighlights', 'maxHighlights', 'segments'],
      },
      validationRules: [
        'Supports two data formats:',
        'Format A (position-based): data.text + data.correctHighlights = [{start, end}, ...]',
        'Format B (segments): data.segments = [{id, text, isCorrect, isTappable}, ...]',
        'At least one correct highlight/segment must exist',
      ],
      examples: ['TAA-LEZEN-DETAIL', 'TAA-SIGNAALWOORD', 'SWW-TT-INV-JIJ'],
    },

    // ─── VISUAL DATA TYPES ──────────────────────────────────────────────────────

    bar_chart: {
      type: 'bar_chart',
      category: 'visual-data',
      renderer: 'src/components/practice/renderers/BarChartRenderer.tsx',
      description: 'Read and interpret bar charts',
      requiredFields: ['question', 'data', 'options', 'correctAnswer'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      // GSR-18 (PR-6): validateBarChart accepts canonical + legacy shapes.
      hasComprehensiveValidator: true,
      dataStructure: {
        required: [],
        optional: [
          'data',
          'labels',
          'values',
          'title',
          'xAxisLabel',
          'yAxisLabel',
          'correctIndex',
          'hideValues',
          'barColor',
        ],
      },
      validationRules: [
        'Supports two data formats:',
        'Format A (canonical): data.data = [{label, value}, ...] — at least 2 entries',
        'Format B (legacy): data.labels + data.values arrays — same length',
        'Each entry must have a string label and numeric value',
      ],
      examples: ['STU-GRAFIEK', 'STU-STAAF'],
    },

    line_chart: {
      type: 'line_chart',
      category: 'visual-data',
      renderer: 'src/components/practice/renderers/LineChartRenderer.tsx',
      description: 'Read and interpret line charts',
      requiredFields: ['question', 'data', 'options', 'correctAnswer'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      // GSR-18 (PR-6): validateLineChart accepts canonical + legacy shapes.
      hasComprehensiveValidator: true,
      dataStructure: {
        required: [],
        optional: [
          'data',
          'labels',
          'values',
          'title',
          'xAxisLabel',
          'yAxisLabel',
          'correctIndex',
          'hideValues',
        ],
      },
      validationRules: [
        'Supports two data formats:',
        'Format A (canonical): data.data = [{label, value}, ...] — at least 2 entries',
        'Format B (legacy): data.labels + data.values arrays — same length',
        'Each entry must have a string label and numeric value',
      ],
      examples: ['STU-LIJN', 'TIJD-DIAGRAM'],
    },

    schedule_reading: {
      type: 'schedule_reading',
      category: 'visual-data',
      renderer: 'src/components/practice/renderers/ScheduleRenderer.tsx',
      description: 'Read and interpret schedules/timetables',
      requiredFields: ['question', 'data', 'options', 'correctAnswer'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['headers', 'rows'],
        optional: ['title', 'highlightCell'],
      },
      validationRules: [
        'data.headers must be array of strings',
        'data.rows must be array of arrays',
      ],
      examples: ['TIJD-SCHEMA', 'TIJD-PLANNING'],
    },

    pie_chart: {
      type: 'pie_chart',
      category: 'visual-data',
      renderer: 'src/components/practice/renderers/PieChartRenderer.tsx',
      description: 'Read and interpret pie/donut charts by clicking segments',
      requiredFields: ['question', 'data', 'options', 'correctAnswer'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      // GSR-18 (PR-6): validatePieChart handles labels/values + correctIndex.
      hasComprehensiveValidator: true,
      dataStructure: {
        required: [],
        optional: [
          'labels',
          'values',
          'correctIndex',
          'title',
          'colors',
          'hideValues',
        ],
      },
      validationRules: [
        'data.labels and data.values must have same length',
        'data.values must be array of numbers',
        'data.correctIndex must be valid index into labels/values',
        'data.labels must be array of non-empty strings',
      ],
      examples: ['STU-CIRKEL'],
    },

    // ─── SPECIALIZED TYPES ──────────────────────────────────────────────────────

    cijferend_rekenen: {
      type: 'cijferend_rekenen',
      category: 'specialized',
      renderer:
        'src/components/practice/renderers/ColumnArithmeticRenderer.tsx',
      description:
        'Column arithmetic (add/subtract/multiply) with step-by-step visualization',
      requiredFields: ['question', 'data'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      structuredDataField: 'columnArithmetic',
      dataStructure: {
        required: ['operand1', 'operand2', 'operation'],
        optional: ['acceptedAnswers', 'showSteps', 'currentStep'],
      },
      validationRules: [
        'data.operand1 and data.operand2 must be positive integers',
        'data.operation must be one of: +, -, ×',
        'If operation is -, data.operand1 must be >= data.operand2',
      ],
      visualRequirements: {
        stimulus: ['column'],
        support: ['column'],
      },
      examples: ['REK-CIJF-PLUS', 'REK-CIJF-MIN', 'REK-CIJF-KEER'],
    },

    /**
     * Defense-in-depth: cijferend_aftrekken/vermenigvuldigen are NOT in the
     * QuestionType union (content uses 'cijferend_rekenen' for +/−/×).
     * These definitions exist so typeStage validation still works if the
     * union is ever expanded, and to document the expected data shape.
     */
    cijferend_aftrekken: {
      type: 'cijferend_aftrekken',
      category: 'specialized',
      renderer:
        'src/components/practice/renderers/ColumnArithmeticRenderer.tsx',
      description: 'Column subtraction with step-by-step visualization',
      requiredFields: ['question', 'data'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      structuredDataField: 'columnArithmetic',
      dataStructure: {
        required: ['operand1', 'operand2'],
        optional: ['showSteps', 'currentStep'],
      },
      validationRules: [
        'data.operand1 must be >= data.operand2',
        'Both operands must be positive integers',
      ],
      visualRequirements: {
        stimulus: ['column'],
        support: ['column'],
      },
      examples: ['REK-CIJF-MIN'],
    },

    cijferend_vermenigvuldigen: {
      type: 'cijferend_vermenigvuldigen',
      category: 'specialized',
      renderer:
        'src/components/practice/renderers/ColumnArithmeticRenderer.tsx',
      description: 'Column multiplication with step-by-step visualization',
      requiredFields: ['question', 'data'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      structuredDataField: 'columnArithmetic',
      dataStructure: {
        required: ['operand1', 'operand2'],
        optional: ['acceptedAnswers', 'showSteps', 'currentStep'],
      },
      validationRules: [
        'data.operand1 and data.operand2 must be positive integers',
      ],
      visualRequirements: {
        stimulus: ['column'],
        support: ['column'],
      },
      examples: ['REK-CIJF-KEER'],
    },

    cijferend_delen: {
      type: 'cijferend_delen',
      category: 'specialized',
      renderer: 'src/components/practice/renderers/LongDivisionRenderer.tsx',
      description: 'Long division with step-by-step visualization',
      requiredFields: ['question', 'data'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      structuredDataField: 'longDivision',
      dataStructure: {
        required: ['operand1', 'operand2'],
        optional: ['acceptedAnswers', 'showSteps', 'currentStep'],
      },
      validationRules: [
        'data.operand1 and data.operand2 must be positive integers',
        'data.operand2 must not be zero',
      ],
      visualRequirements: {
        stimulus: [],
        support: ['column'],
      },
      examples: ['REK-CIJF-DEEL'],
    },

    clock: {
      type: 'clock',
      category: 'specialized',
      renderer: 'src/components/practice/renderers/ClockRenderer.tsx',
      description: 'Clock reading and time interpretation (MeesterJay 4.1)',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'options', 'correctAnswer'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['mode', 'target'],
        optional: ['analog', 'digital', 'options'],
      },
      validationRules: [
        'data.mode must be one of: read_analog_fill, read_digital_fill, analog_to_digital_mc, digital_to_analog_mc',
        'data.target must have hours (0-23) and minutes (0-59)',
        'If mode ends with _mc, options array is required',
      ],
      visualRequirements: {
        stimulus: ['clock', 'digitalClock'],
        support: ['clock', 'digitalClock', 'rule'],
      },
      examples: ['TIJD-ANALOOG', 'TIJD-DIGITAAL', 'TIJD-CONVERSIE'],
    },

    clock_set: {
      type: 'clock_set',
      category: 'specialized',
      renderer: 'src/components/practice/renderers/ClockSetRenderer.tsx',
      description: 'Interactive clock setting by dragging hands',
      requiredFields: ['question', 'data'],
      optionalFields: ['context'],
      cognitiveCeiling: 'DOK2',
      // GSR-18 (PR-6): clock_set reuses the singular clock's nested
      // data.clock shape. Replaces the hardcoded TYPE_DATA_ALIASES in typeStage.
      dataKeyAlias: 'clock',
      dataStructure: {
        required: ['mode', 'target'],
        optional: ['analog'],
      },
      validationRules: [
        'data.mode must be set_analog',
        'data.target must have hours (0-23) and minutes (0-59)',
      ],
      visualRequirements: {
        stimulus: ['clock'],
        support: ['clock', 'rule'],
      },
      examples: ['TIJD-ANALOOG'],
    },

    money: {
      type: 'money',
      category: 'specialized',
      renderer: 'src/components/practice/renderers/MoneyAssemblerRenderer.tsx',
      description: 'Money assembly and change calculation (MeesterJay 4.1)',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'options', 'correctAnswer'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['mode', 'targetCents', 'allowedDenoms'],
        optional: ['priceCents', 'paidCents', 'constraints', 'options'],
      },
      validationRules: [
        'data.mode must be: make_amount or give_change',
        'data.targetCents must be positive integer',
        'data.allowedDenoms must be array of {valueCents, type, label}',
        'If mode is give_change, priceCents and paidCents are required',
      ],
      visualRequirements: {
        stimulus: ['money'],
        support: ['money', 'rule'],
      },
      examples: ['GELD-MAAK', 'GELD-TERUG', 'GELD-WISSEL'],
    },

    multiplication_grid: {
      type: 'multiplication_grid',
      category: 'specialized',
      renderer:
        'src/components/practice/renderers/MultiplicationGridRenderer.tsx',
      description:
        'Interactive multiplication grid/table with hidden cells to fill in',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['mode'],
        optional: ['baseNumber', 'range', 'hiddenCells', 'rows', 'cols'],
      },
      validationRules: [
        'data.mode must be one of: single_table, cross_table',
        'If mode is single_table: data.baseNumber and data.range required, data.hiddenCells = indices into range',
        'If mode is cross_table: data.rows and data.cols required, data.hiddenCells = [{row, col}]',
      ],
      visualRequirements: {
        stimulus: [],
        support: [],
      },
      examples: ['REK-TAFELS-BASIS', 'REK-TAFELS-UITBREIDEN'],
    },

    true_false_series: {
      type: 'true_false_series',
      category: 'interactive',
      renderer: 'src/components/practice/renderers/TrueFalseSeriesRenderer.tsx',
      description:
        'Series of statements to evaluate as true or false. ' +
        'DIDACTIC NOTE (P0 audit fix, mei 2026): cognitive ceiling lowered from DOK3 → DOK2. ' +
        'In practice true_false items rarely reach DOK3 — they are recognition tasks. ' +
        'Use this type ONLY for genuine factual judgment families (TAA-LEZEN-FEIT). ' +
        'For DOK3 diversity, prefer: categorize, error_spotting, comparison_judge, sentence_error_judge, step_solver, strategy_solver, sentence_parser.',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['statements'],
        optional: [],
      },
      validationRules: [
        'data.statements must be an array of objects with { text: string, isTrue: boolean }',
        'At least 2 statements required',
      ],
      examples: ['TAA-LEZEN-FEIT'],
    },

    estimation_slider: {
      type: 'estimation_slider',
      category: 'interactive',
      renderer:
        'src/components/practice/renderers/EstimationSliderRenderer.tsx',
      description: 'Estimate a value on a number line',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['min', 'max', 'correctValue', 'tolerancePercent'],
        optional: ['unit', 'showNumberLine'],
      },
      validationRules: [
        'data.min must be less than data.max',
        'data.correctValue must be between min and max',
        'data.tolerancePercent must be a number between 1 and 50',
      ],
      examples: ['GET-GROOT-SCHATTEN'],
    },

    categorize: {
      type: 'categorize',
      category: 'interactive',
      renderer: 'src/components/practice/renderers/CategorizeRenderer.tsx',
      description: 'Sort items into categories using tap-to-assign interaction',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids'],
      cognitiveCeiling: 'DOK3',
      dataStructure: {
        required: ['categories', 'items'],
        optional: [],
      },
      validationRules: [
        'data.categories must be an array of at least 2 category name strings',
        'data.items must be an array of objects with { text: string, correctCategory: number }',
        'Each item.correctCategory must be a valid index into data.categories',
        'At least 4 items required',
      ],
      examples: ['TAA-SOORT-WW', 'TAA-SIGNAALWOORD', 'SPR-KLANK-JAGER'],
    },

    error_spotting: {
      type: 'error_spotting',
      category: 'interactive',
      renderer: 'src/components/practice/renderers/ErrorSpottingRenderer.tsx',
      description: 'Find and correct errors in a text passage',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids'],
      cognitiveCeiling: 'DOK3',
      dataStructure: {
        required: ['text', 'errors'],
        optional: [],
      },
      validationRules: [
        'data.text must be a non-empty string',
        'data.errors must be an array of objects with { word: string, position: number, correction: string }',
        'Each error position must be a valid word index in the text',
        'At least 1 error required',
      ],
      examples: ['SWW-VD-HOMOFOON', 'SPR-WEET-DT', 'SWW-TT-INV-JIJ'],
    },

    grid_shade: {
      type: 'grid_shade',
      category: 'interactive',
      renderer: 'src/components/practice/renderers/GridShadeRenderer.tsx',
      description: 'Tap to shade cells in a grid to match a count or fraction',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['gridRows', 'gridCols', 'targetCount', 'mode'],
        optional: ['preShaded', 'showCounter'],
      },
      validationRules: [
        'data.gridRows and data.gridCols must be positive integers',
        'data.targetCount must be a positive integer <= gridRows * gridCols',
        'data.mode must be one of: shade_count, shade_fraction, shade_to_match',
      ],
      examples: ['GET-BREUK-BASIS', 'GET-BREUK-DEEL'],
    },

    step_solver: {
      type: 'step_solver',
      category: 'interactive',
      renderer: 'src/components/practice/renderers/StepSolverRenderer.tsx',
      description: 'Solve a problem step-by-step with sequential validation',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids'],
      cognitiveCeiling: 'DOK3',
      dataStructure: {
        required: ['steps'],
        optional: ['context', 'showStepsProgressively'],
      },
      validationRules: [
        'data.steps must be an array of objects with { prompt, correctAnswer }',
        'At least 1 step required',
      ],
      examples: ['REK-CONTEXT-MEERSTAPS', 'MET-OPP-SAMENGESTELD'],
    },

    sentence_parser: {
      type: 'sentence_parser',
      category: 'interactive',
      renderer: 'src/components/practice/renderers/SentenceParserRenderer.tsx',
      description: 'Assign grammatical labels to words in a sentence',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids'],
      cognitiveCeiling: 'DOK3',
      dataStructure: {
        required: ['sentence', 'words', 'labels', 'correctAssignments'],
        optional: [],
      },
      validationRules: [
        'data.words must be an array of strings',
        'data.labels must be an array of strings',
        'data.correctAssignments must be a record mapping word indices to labels',
      ],
      examples: ['TAA-ONT-BASIS', 'TAA-ONT-GEZEGDE'],
    },

    comparison_judge: {
      type: 'comparison_judge',
      category: 'interactive',
      renderer: 'src/components/practice/renderers/ComparisonJudgeRenderer.tsx',
      description: 'Compare two or more items based on a criterion',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids'],
      cognitiveCeiling: 'DOK3',
      dataStructure: {
        required: ['items', 'criterion', 'correctIndex'],
        optional: [],
      },
      validationRules: [
        'data.items must be an array of objects with { id, content }',
        'At least 2 items required',
        'data.correctIndex must be a valid index into data.items',
      ],
      examples: ['GET-KOMMA-VERGELIJK', 'GET-BREUK-VERGELIJK'],
    },

    thermometer_slider: {
      type: 'thermometer_slider',
      category: 'interactive',
      renderer:
        'src/practice/components/renderers/ThermometerSliderRenderer.tsx',
      description:
        'Read or set a temperature on an interactive thermometer slider',
      requiredFields: ['question', 'data', 'correctAnswer'],
      optionalFields: ['context', 'stimulusAids', 'supportAids'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['minTemp', 'maxTemp', 'step'],
        optional: ['startTemp', 'unit', 'showScale'],
      },
      validationRules: [
        'data.minTemp must be less than data.maxTemp',
        'data.step must be a positive number',
        'correctAnswer must be a number between minTemp and maxTemp',
        'If data.unit provided, must be a string (e.g. "°C")',
      ],
      examples: ['GET-NEGATIEF-MIN', 'GET-NEGATIEF-PLUS'],
    },

    sentence_error_judge: {
      type: 'sentence_error_judge',
      category: 'interactive',
      renderer:
        'src/practice/components/renderers/SentenceErrorJudgeRenderer.tsx',
      description:
        'Pick which sentence in a set of 4 contains a spelling or grammar error',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids'],
      cognitiveCeiling: 'DOK3',
      dataStructure: {
        required: ['sentences'],
        optional: [],
      },
      validationRules: [
        'data.sentences must be an array of objects with { text: string, hasError: boolean }',
        'At least 2 sentences required',
        'Exactly one sentence must have hasError: true',
        'Sentences with hasError: true must include correction (string)',
        'Optionally include explanation (string) for didactic feedback',
      ],
      examples: [
        'SWW-TT-ZIJN-HEBBEN',
        'SWW-VD-STERK',
        'SWW-VD-ZONDER-GE',
        'SWW-VT-VZFS',
      ],
    },

    map_locate_region: {
      type: 'map_locate_region',
      category: 'interactive',
      renderer: 'src/practice/components/renderers/MapLocateRegionRenderer.tsx',
      description:
        'Click on the correct region (province, country, continent) on a geographic map',
      requiredFields: ['question', 'data', 'correctAnswer'],
      optionalFields: ['context', 'stimulusAids', 'supportAids', 'feedback'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['mapId', 'correctRegionId'],
        optional: ['showLabels', 'showFirstLetterHint'],
      },
      validationRules: [
        'data.mapId must be a known atlas id (e.g. nl-provinces, eu-countries, world-countries)',
        'data.correctRegionId must exist in the referenced atlas',
        'data.showLabels (optional) must be one of: "all" | "highlighted" | "none"',
        'data.showFirstLetterHint (optional) must be boolean',
      ],
      examples: ['TOPO-NL-PROV (map variants)'],
    },

    map_locate_point: {
      type: 'map_locate_point',
      category: 'interactive',
      renderer: 'src/practice/components/renderers/MapLocatePointRenderer.tsx',
      description:
        'Click on the map to mark a location — either freely with haversine tolerance (mode: free) or by picking a predefined dot (mode: snap-to-dot)',
      requiredFields: ['question', 'data', 'correctAnswer'],
      optionalFields: ['context', 'stimulusAids', 'supportAids', 'feedback'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['mapId'],
        optional: [
          'mode',
          'targetPoint',
          'toleranceKm',
          'candidateCityIds',
          'correctCityId',
          'focusRegionId',
          'referenceCityIds',
          'showLabels',
        ],
      },
      validationRules: [
        'data.mapId must be a known atlas id (e.g. nl-provinces, eu-countries)',
        'If mode is "free" (default): data.targetPoint and data.toleranceKm are required',
        'If mode is "snap-to-dot": data.candidateCityIds and data.correctCityId are required',
        'data.referenceCityIds (optional) replaces legacy showOtherCities',
      ],
      examples: ['TOPO-NL-STEDEN (map variants)'],
    },

    map_locate_line: {
      type: 'map_locate_line',
      category: 'interactive',
      renderer: 'src/practice/components/renderers/MapLocateLineRenderer.tsx',
      description:
        'Click on the correct river or waterway. Validator uses point-to-polyline haversine distance against the target polyline from atlases/nl/rivers.json.',
      requiredFields: ['question', 'data', 'correctAnswer'],
      optionalFields: ['context', 'stimulusAids', 'supportAids', 'feedback'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['mapId', 'targetRiverId'],
        optional: ['toleranceKm', 'referenceCityIds'],
      },
      validationRules: [
        'data.mapId must be a known atlas id (e.g. nl-provinces)',
        'data.targetRiverId must match an id in src/content/atlases/nl/rivers.json (e.g. "NL-RIJN")',
        'data.toleranceKm must be a positive number (typically 8-15 km)',
      ],
      examples: ['TOPO-NL-RIVIEREN (river click variants)'],
    },

    // NOTE: map_label_regions was DEPRECATED in Sessie 3 (mei 2026) — 0 seed usage.

    map_place_cities: {
      type: 'map_place_cities',
      category: 'interactive',
      renderer: 'src/practice/components/renderers/MapPlaceCitiesRenderer.tsx',
      description:
        'Place an ordered list of named cities on the map (plus-niveau, tier 3+)',
      requiredFields: ['question', 'data', 'correctAnswer'],
      optionalFields: ['context', 'stimulusAids', 'supportAids', 'feedback'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['mapId', 'targetPoints', 'toleranceKm'],
        optional: [],
      },
      validationRules: [
        'data.mapId must be a known atlas id (e.g. nl-provinces)',
        'data.targetPoints must be a non-empty array of { name: string, lat: number, lon: number }',
        'data.toleranceKm must be a positive number (typically 25–40)',
        'At least 2 targetPoints required',
      ],
      examples: ['TOPO-NL-STEDEN (plus variants)'],
    },

    // ─── Types added mei 2026 (previously missing from governance sidecar) ─────
    // These types exist in the runtime registry and have working renderers/seeds,
    // but were missing here — causing false-positive DIVERSITY_NO_INTERACTIVE for
    // families using them (GET-PROC-VAN bar_model_part, GET-WORTEL
    // square_root_builder, GET-GROOT-POS place_value_build, REK-HANDIG-*
    // strategy_solver). Adding them once here is no longer required for diversity
    // (the stage now reads isInteractive from the runtime registry), but is still
    // valuable for cognitiveCeiling, dataStructure requirements, and DOK3 routing.

    bar_model_part: {
      type: 'bar_model_part',
      category: 'specialized',
      renderer: 'src/practice/components/renderers/BarModelPartRenderer.tsx',
      description:
        'Singapore-style bar model: split the whole strip into proportional parts (deel-van-geheel)',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids', 'feedback'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['barModelPart'],
        optional: [],
      },
      validationRules: [
        'data.barModelPart must be an object with { total, parts, correctValue } or equivalent shape',
        'Renderer requires explicit total — never inferred from correctAnswer',
      ],
      visualRequirements: {
        stimulus: ['barModel', 'fraction', 'fractionStrip'],
        support: ['barModel', 'fraction', 'rule'],
      },
      examples: ['GET-PROC-VAN', 'GET-BREUK-DEEL'],
    },

    place_value_build: {
      type: 'place_value_build',
      category: 'specialized',
      renderer: 'src/practice/components/renderers/PlaceValueBuildRenderer.tsx',
      description:
        'MAB-blokjes: build a number by combining duizendtallen / honderdtallen / tientallen / eenheden',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids', 'feedback'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['placeValueBuild'],
        optional: [],
      },
      validationRules: [
        'data.placeValueBuild must define the target number and allowed denominations (D/H/T/E)',
        'targetNumber must be a positive integer',
      ],
      visualRequirements: {
        stimulus: ['placeValueBlocks'],
        support: ['placeValueBlocks', 'rule'],
      },
      examples: ['GET-GROOT-POS', 'GET-GROOT-LEZEN'],
    },

    square_root_builder: {
      type: 'square_root_builder',
      category: 'specialized',
      renderer:
        'src/practice/components/renderers/SquareRootBuilderRenderer.tsx',
      description:
        'Build a square on a grid to visualize kwadraten / wortels (Reken Zeker pattern)',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids', 'feedback'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['squareRootBuilder'],
        optional: [],
      },
      validationRules: [
        'data.squareRootBuilder must define the target value (n² or √n) and grid size',
        'Renderer expects explicit grid dimensions',
      ],
      visualRequirements: {
        stimulus: ['squareGrid'],
        support: ['squareGrid', 'rule'],
      },
      examples: ['GET-WORTEL', 'GET-MACHT'],
    },

    strategy_solver: {
      type: 'strategy_solver',
      category: 'interactive',
      renderer: 'src/practice/components/renderers/StrategySolverRenderer.tsx',
      description:
        'Pick a calculation strategy ("handig rekenen") and apply it — DOK3 by design',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids', 'feedback'],
      cognitiveCeiling: 'DOK3',
      dataStructure: {
        required: ['strategySolver'],
        optional: [],
      },
      validationRules: [
        'data.strategySolver must define { strategies: StrategySolverStrategy[], correctStrategyId, finalAnswer }',
        'At least 2 strategies required — one of which is the correct/most-efficient choice',
        'See docs/specs/STRATEGY_SOLVER_TYPE_2026.md',
      ],
      examples: ['REK-HANDIG-KEER', 'REK-HANDIG-DEEL', 'REK-HANDIG-PLUS-MIN'],
    },

    double_number_line: {
      type: 'double_number_line',
      category: 'specialized',
      renderer:
        'src/practice/components/renderers/DoubleNumberLineRenderer.tsx',
      description:
        'Dubbele getallenlijn: twee gekoppelde schalen (bijv. % vs. aantal, of km vs. uur) waarop de student een waarde aanwijst. Adresseert evenredig redeneren door de proportionele verhouding tussen de twee schalen visueel te maken.',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids', 'feedback'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['doubleNumberLine'],
        optional: [],
      },
      validationRules: [
        'data.doubleNumberLine must define { topScale, bottomScale, correctValue }',
        'topScale and bottomScale must each have { min, max, unit, label }',
        'correctValue must fall within the active scale range',
      ],
      visualRequirements: {
        stimulus: [],
        support: ['rule'],
      },
      examples: ['GET-PROC-VAN', 'GET-VERH-RECEPT'],
    },

    fraction_equivalence_match: {
      type: 'fraction_equivalence_match',
      category: 'interactive',
      renderer:
        'src/practice/components/renderers/FractionEquivalenceMatchRenderer.tsx',
      description:
        'Koppel breuken aan hun gelijkwaardige vormen (bijv. 1/2 ↔ 2/4 ↔ 5/10). De student matcht items uit een linker kolom met items uit een rechter kolom om te tonen dat verschillende breuken dezelfde waarde kunnen hebben.',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids', 'feedback'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['fractionEquivalenceMatch'],
        optional: [],
      },
      validationRules: [
        'data.fractionEquivalenceMatch must define { pairs, distractors? }',
        'Each pair must reference two equivalent fraction values',
        'At least 3 pairs required for a meaningful matching task',
      ],
      visualRequirements: {
        stimulus: ['fraction', 'fractionStrip'],
        support: ['fraction', 'rule'],
      },
      examples: ['GET-BREUK-UITBREIDEN', 'GET-BREUK-VEREENVOUDIG'],
    },

    fraction_strip_builder: {
      type: 'fraction_strip_builder',
      category: 'specialized',
      renderer:
        'src/practice/components/renderers/FractionStripBuilderRenderer.tsx',
      description:
        'Bouw een breukenstrook: deel een strook in n gelijke delen en arceer m daarvan om m/n te tonen. Adresseert het deel-van-geheel concept door de student de noemer (verdeling) en teller (gearceerde delen) zelf te laten construeren.',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids', 'feedback'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['fractionStripBuilder'],
        optional: [],
      },
      validationRules: [
        'data.fractionStripBuilder must define { targetNumerator, targetDenominator }',
        'targetDenominator must be a positive integer >= 2',
        'targetNumerator must be between 0 and targetDenominator inclusive',
      ],
      visualRequirements: {
        stimulus: ['fractionStrip'],
        support: ['fractionStrip', 'fraction', 'rule'],
      },
      examples: ['GET-BREUK-BASIS', 'GET-BREUK-RASTER'],
    },

    comma_shift: {
      type: 'comma_shift',
      category: 'specialized',
      renderer: 'src/practice/components/renderers/CommaShiftRenderer.tsx',
      description:
        'Komma-verschuiver — kommagetal in plaatswaarde-cellen waarbij de student de ' +
        'komma stap voor stap naar links of rechts schuift bij ×10, ×100, ÷10 of ÷100. ' +
        'Cijfers blijven op hun plek staan; alleen de komma beweegt. Adresseert de ' +
        'canonieke misconceptie "×10 = nul erbij" door de plaatswaarde-shift expliciet ' +
        'zichtbaar te maken. Schoolbook-anker: Pluspunt / WiG groep 6–7.',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids', 'feedback'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['commaShift'],
        optional: [],
      },
      validationRules: [
        'data.commaShift.startValue must be a Dutch decimal string (e.g. "3,45" or "12")',
        'data.commaShift.operation must be one of "×10" | "×100" | "÷10" | "÷100"',
        'data.commaShift.correctAnswer must be a Dutch decimal string consistent with applying operation to startValue',
        'data.commaShift.acceptedAnswers (optional) lists extra accepted Dutch decimal schrijfwijzen',
        'data.commaShift.showAnimation (optional, default true)',
      ],
      visualRequirements: {
        stimulus: [],
        support: ['placeValueBlocks', 'rule'],
      },
      examples: ['GET-KOMMA-KEER', 'GET-KOMMA-DEEL', 'GET-KOMMA-WAARDE'],
    },

    scientific_notation_builder: {
      type: 'scientific_notation_builder',
      category: 'specialized',
      renderer:
        'src/practice/components/renderers/ScientificNotationBuilderRenderer.tsx',
      description:
        'Wetenschappelijke-notatie bouwer — student typt de coëfficiënt (1 ≤ a < 10) en kiest de exponent (0–8) om een doelgetal als a × 10ⁿ uit te drukken. Adresseert de plaatswaarde-ladder bij grote getallen en de canonieke-vorm-eis. Schoolbook-anker: GET-WETENSCHAPPELIJK / GET-WETENSCHAP-REKEN (groep 8).',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids', 'feedback'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['scientificNotationBuilder'],
        optional: [],
      },
      validationRules: [
        'data.scientificNotationBuilder.targetValue must be a positive finite number',
        'data.scientificNotationBuilder.minExponent / maxExponent (optional) must be non-negative integers with min ≤ max',
        'Validator enforces both product correctness AND canonical form (1 ≤ a < 10)',
      ],
      visualRequirements: {
        stimulus: ['powersOfTenStrip'],
        support: ['powersOfTenStrip', 'rule'],
      },
      examples: ['GET-WETENSCHAPPELIJK', 'GET-WETENSCHAP-REKEN'],
    },

    spelling_pick: {
      type: 'spelling_pick',
      category: 'specialized',
      renderer: 'src/practice/components/renderers/SpellingPickRenderer.tsx',
      description:
        'Letterkeuze-cloze (SPR specialist) — een Nederlands woord met een gat (before + [gat] + after) waar de student de juiste grafeemtegel aantikt (ei vs ij, au vs ou, d vs t). Productief (de regel moet toegepast/onthouden worden) maar speels (tikken i.p.v. typen). Vult het gat tussen generiek fill_in en volledige-woord-herkenning voor de recall-zware SPR-families.',
      requiredFields: ['question', 'data'],
      optionalFields: ['context', 'stimulusAids', 'supportAids', 'feedback'],
      cognitiveCeiling: 'DOK2',
      dataStructure: {
        required: ['spellingPick'],
        optional: [],
      },
      validationRules: [
        'data.spellingPick.variant must be one of "ei-ij" | "au-ou" | "d-t" | "custom"',
        'data.spellingPick.graphemes is required when variant === "custom"; otherwise it falls back to the variant default',
        'data.spellingPick.words must be a non-empty array (1–4) of { before, after, correct }',
        'Each word.correct must be one of the resolved graphemes',
      ],
      visualRequirements: {
        stimulus: [],
        support: ['rule'],
      },
      examples: ['SPR-WEET-EI-IJ', 'SPR-KLANK-AU-OU', 'SPR-WEET-D-T'],
    },
  }

// ─── Safe defaults for types missing from the sidecar ─────────────────────────

/**
 * Build a safe default definition for a runtime type that is not enriched
 * in the sidecar. Defaults assume "interactive, DOK2, minimal required
 * fields" — conservative enough not to trigger false-positive blockers,
 * permissive enough that the type is still seen by the diversity stage.
 */
function makeDefaultDefinition(type: QuestionType): QuestionTypeDefinition {
  return {
    type,
    category: 'interactive',
    renderer: '(see runtime registry)',
    description: '(no governance description registered)',
    requiredFields: ['question'],
    optionalFields: ['context', 'stimulusAids', 'supportAids'],
    cognitiveCeiling: 'DOK2',
    validationRules: [],
    examples: [],
  }
}

/**
 * Canonical governance view of all question types.
 *
 * Derived at module-load time by iterating the RUNTIME registry
 * (QUESTION_TYPE_META) and merging in sidecar metadata where present.
 * Missing types get safe defaults + a dev-time console.warn.
 */
export const QUESTION_TYPE_REGISTRY: Record<
  QuestionType,
  QuestionTypeDefinition
> = (() => {
  const result = {} as Record<QuestionType, QuestionTypeDefinition>
  const missing: QuestionType[] = []
  for (const type of Object.keys(QUESTION_TYPE_META) as QuestionType[]) {
    const fromSidecar = SIDECAR_METADATA[type]
    if (fromSidecar) {
      result[type] = fromSidecar
    } else {
      missing.push(type)
      result[type] = makeDefaultDefinition(type)
    }
  }
  if (missing.length > 0 && typeof console !== 'undefined') {
    // Dev-time signal — no blocker, no crash. Adding the type to the
    // sidecar with proper cognitiveCeiling/dataStructure resolves it.
    console.warn(
      `[governance] ${missing.length} runtime question type(s) lack governance metadata; using safe defaults: ${missing.join(', ')}. Add entries to SIDECAR_METADATA in src/governance/questionTypeDefinitions.ts to refine.`,
    )
  }
  return result
})()

// NOTE: stab-ronde 3 (mei 2026) — `getQuestionTypeDefinition` en
// `getQuestionTypesByCategory` verwijderd: nul callers in `src/**`. Consumers
// die een definitielookup nodig hebben kunnen direct `QUESTION_TYPE_REGISTRY[type]`
// gebruiken.

/**
 * Get all question types whose cognitive ceiling reaches DOK3.
 * Used by the diversity stage (DIV-005) to detect families that lack any
 * type capable of probing analytical/evaluative thinking.
 *
 * SSOT: derived from the cognitiveCeiling property on each definition,
 * so the set automatically grows when new DOK3-capable types are added.
 */
export function getDok3CapableTypes(): QuestionType[] {
  return Object.values(QUESTION_TYPE_REGISTRY)
    .filter((def) => def.cognitiveCeiling === 'DOK3')
    .map((def) => def.type)
}

/**
 * Get all question types that REQUIRE a structured-data namespace on
 * `question.data` (e.g. cijferend_rekenen requires `data.columnArithmetic`).
 *
 * SSOT: derived from the `structuredDataField` property on each definition,
 * so adding a new arithmetic / data-first type only requires setting that
 * property in the registry — pedagogyStage picks it up automatically.
 *
 * Used by pedagogyStage.detectsRegexFallback() to know which question
 * types must not rely on regex-parsing of the question text.
 */
export function getTypesRequiringStructuredData(): QuestionType[] {
  return Object.values(QUESTION_TYPE_REGISTRY)
    .filter((def) => def.structuredDataField !== undefined)
    .map((def) => def.type)
}

/**
 * Get the structured-data namespace key required for a given question type,
 * or null when the type has no such requirement.
 *
 * Example: getStructuredDataFieldForType('cijferend_rekenen') → 'columnArithmetic'
 */
export function getStructuredDataFieldForType(
  type: QuestionType,
): 'columnArithmetic' | 'longDivision' | null {
  return QUESTION_TYPE_REGISTRY[type]?.structuredDataField ?? null
}

// NOTE: stab-ronde 3 (mei 2026) — `getQuestionTypesRequiringVisual` en
// `validateQuestionType` verwijderd: nul callers anywhere in `src/**`.
// Validatie van required fields/data-structure gebeurt vandaag de dag al
// in de schema- en type-stages van de pipeline; deze parallelle helper
// dupliceerde die logica zonder gebruikt te worden.
